import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Plus, Loader2, Save, Trash2, Star, Copy, Eye } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { QuotePdfPages } from "@/components/QuotePdfPages";
import { toast } from "sonner";

const BLANK = { name: "", intro: "", terms: "", payment_terms: "", bank_details: "", notes: "", validity_days: 30, tax_percent: 5 };
const NEW = "__new__";

export default function QuotationTemplates() {
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState("");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dupId, setDupId] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const isNew = selId === NEW;
  const sel = isNew ? null : (list.find((t) => t.id === selId) || null);

  const load = async (keepSel) => {
    try {
      const { data } = await api.get("/quote-templates");
      setList(data);
      const next = keepSel && data.find((t) => t.id === keepSel) ? keepSel : (data[0]?.id || "");
      setSelId(next);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (!isNew && sel) setForm({ ...sel }); }, [selId, list]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startNew = () => {
    setSelId(NEW);
    setForm({ ...BLANK });
  };

  const save = async () => {
    if (!form) return;
    const name = (form.name || "").trim();
    if (!name) { toast.error("Give the template a name before saving"); return; }
    const payload = {
      name, intro: form.intro, terms: form.terms, payment_terms: form.payment_terms,
      bank_details: form.bank_details, notes: form.notes, validity_days: parseInt(form.validity_days) || 30,
      tax_percent: parseFloat(form.tax_percent) || 0,
    };
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/quote-templates", payload);
        await load(data.id);
        toast.success("Template created — you can now add cover/closing pages");
      } else {
        await api.put(`/quote-templates/${selId}`, payload);
        await load(selId);
        toast.success("Template saved");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quotation template? This cannot be undone.")) return;
    try {
      await api.delete(`/quote-templates/${id}`);
      await load(id === selId ? "" : selId);
      toast.success("Template deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.post(`/quote-templates/${id}/set-default`);
      await load(selId);
      toast.success("Default template updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const duplicate = async (id) => {
    setDupId(id);
    try {
      const { data } = await api.post(`/quote-templates/${id}/duplicate`);
      await load(data.id);
      toast.success(`Duplicated as "${data.name}"`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not duplicate");
    } finally {
      setDupId("");
    }
  };

  const openPreview = async (id) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl("");
    try {
      const { data } = await api.get(`/quote-templates/${id}/preview.pdf`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(new Blob([data], { type: "application/pdf" })));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build the preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setPreviewOpen(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Quotations</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Quotation Templates</h1>
          <p className="text-muted-foreground text-sm mt-1">Save reusable intro, terms, bank details, notes, validity and cover/closing pages — then autofill any quote in one click.</p>
        </div>
        <Button data-testid="new-template-button" onClick={startNew} className="gap-2 h-11 font-semibold shrink-0">
          <Plus className="w-4 h-4" /> New template
        </Button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List */}
        <div className="space-y-2" data-testid="template-list">
          {isNew && (
            <div data-testid="template-row-new" className="w-full text-left bg-card border border-primary ring-1 ring-primary/30 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 shrink-0 text-primary" />
                <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate flex-1">{form?.name?.trim() || "New template"}</span>
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-0 text-[10px] px-1.5">Unsaved</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate pl-6">Fill in the fields and click Create template</div>
            </div>
          )}
          {list.length === 0 && !isNew && (
            <div className="bg-card border border-dashed border-border rounded-xl p-6 text-center text-sm text-muted-foreground">
              No templates yet. Create your first one.
            </div>
          )}
          {list.map((t) => (
            <button
              key={t.id}
              data-testid={`template-row-${t.id}`}
              onClick={() => setSelId(t.id)}
              className={`w-full text-left bg-card border rounded-xl px-4 py-3 transition-colors ${t.id === selId ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
            >
              <div className="flex items-center gap-2">
                <FileText className={`w-4 h-4 shrink-0 ${t.id === selId ? "text-primary" : "text-muted-foreground"}`} />
                <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate flex-1">{t.name}</span>
                {t.is_default && <Badge data-testid={`template-default-badge-${t.id}`} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 text-[10px] px-1.5">Default</Badge>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate pl-6">
                {t.terms || t.intro || "Empty — click to edit"}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {!form || (!isNew && !sel) ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              Select a template on the left, or create a new one.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl shadow-sm" data-testid="template-editor">
              <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-b border-border">
                <div className="font-semibold text-slate-900 dark:text-slate-100">{isNew ? "New template" : "Edit template"}</div>
                {!isNew && sel && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" data-testid="preview-template-button" onClick={() => openPreview(sel.id)} className="gap-1.5 h-8">
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" data-testid="duplicate-template-button" onClick={() => duplicate(sel.id)} disabled={dupId === sel.id} className="gap-1.5 h-8">
                    {dupId === sel.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Duplicate
                  </Button>
                  {!sel.is_default && (
                    <Button variant="outline" size="sm" data-testid="set-default-button" onClick={() => makeDefault(sel.id)} className="gap-1.5 h-8">
                      <Star className="w-3.5 h-3.5" /> Set default
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" data-testid="delete-template-button" onClick={() => remove(sel.id)} className="gap-1.5 h-8 text-rose-600 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </div>
                )}
              </div>
              <div className="p-6 space-y-5">
                <div className="grid sm:grid-cols-4 gap-4">
                  <div className="sm:col-span-2">
                    <Label>Template name</Label>
                    <Input data-testid="template-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="e.g. Civil works — standard T&C" />
                  </div>
                  <div>
                    <Label>Validity (days)</Label>
                    <Input data-testid="template-validity-input" type="number" value={form.validity_days} onChange={set("validity_days")} className="mt-1.5 font-mono" />
                  </div>
                  <div>
                    <Label>Tax %</Label>
                    <Input data-testid="template-tax-input" type="number" step="0.01" value={form.tax_percent} onChange={set("tax_percent")} className="mt-1.5 font-mono" />
                  </div>
                </div>
                <div>
                  <Label>Intro / cover note to client</Label>
                  <Textarea data-testid="template-intro-input" value={form.intro} onChange={set("intro")} rows={3} className="mt-1.5" placeholder="We are pleased to submit our quotation for your kind consideration…" />
                </div>
                <div>
                  <Label>Terms &amp; Conditions</Label>
                  <Textarea data-testid="template-terms-input" value={form.terms} onChange={set("terms")} rows={4} className="mt-1.5" placeholder="Payment terms, delivery, validity…" />
                </div>
                <div>
                  <Label>Payment terms</Label>
                  <Textarea data-testid="template-payment-input" value={form.payment_terms} onChange={set("payment_terms")} rows={2} className="mt-1.5" placeholder="e.g. 50% advance, balance on delivery" />
                </div>
                <div>
                  <Label>Bank details</Label>
                  <Textarea data-testid="template-bank-input" value={form.bank_details} onChange={set("bank_details")} rows={3} className="mt-1.5" placeholder="Bank name, account name, IBAN, account no." />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea data-testid="template-notes-input" value={form.notes} onChange={set("notes")} rows={2} className="mt-1.5" placeholder="Default notes for this template" />
                </div>
                <div className="pt-1">
                  <Label className="mb-1 block">Cover &amp; closing PDF pages</Label>
                  {isNew ? (
                    <p className="text-xs text-muted-foreground">Save the template first, then upload cover &amp; closing pages here.</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-3">Wrapped around any quotation that uses this template.</p>
                      <QuotePdfPages scope={`template:${sel.id}`} value={sel} onChange={() => load(sel.id)} testid="template-quote-pdf" />
                    </>
                  )}
                </div>
                <div className="flex justify-end pt-2 border-t border-border">
                  <Button data-testid="save-template-button" onClick={save} disabled={saving || !(form.name || "").trim()} className="gap-2 h-11 font-semibold">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isNew ? "Create template" : "Save template"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Template preview modal */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent data-testid="template-preview-dialog" className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base"><Eye className="w-4 h-4" /> Template preview — sample quotation</DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-secondary min-h-0">
            {previewLoading || !previewUrl ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">Building preview…</span>
              </div>
            ) : (
              <iframe data-testid="template-preview-frame" title="Template preview" src={previewUrl} className="w-full h-full border-0" />
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-border">
            <Button variant="outline" onClick={closePreview}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
